import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Image as KImage, Transformer } from 'react-konva';

/* ── Shared canvas constants ── */
const DESIGN_W = 1080;
const DESIGN_H = 1080;

/* ── useKonvaImage hook ── */
function useKonvaImage(url) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!url) { setImage(null); return; }
    const img = new window.Image();
    // Don't set crossOrigin — allows external images to display on canvas
    // Canvas becomes tainted but we use FFmpeg for export, not canvas.toDataURL()
    img.onload = () => setImage(img);
    img.onerror = () => {
      // Fallback: try through backend proxy if direct load fails
      if (url.startsWith('http') && !img._proxied) {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        img._proxied = true;
        let token = '';
        try { token = window.localStorage.getItem('token') || ''; } catch (e) { token = ''; }
        const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
        img.src = `${API}/api/proxy-image?url=${encodeURIComponent(url)}${tokenParam}`;
      } else {
        setImage(null);
      }
    };
    img.src = url;
  }, [url]);
  return image;
}

/* ── Interactive Layer (drag, resize, edit) ── */
const InteractiveLayer = ({ layer, SX, SY, isSelected, onSelect, onUpdate, isPlaying }) => {
  const [editing, setEditing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null); // which handle: 'nw','n','ne','e','se','s','sw','w'
  const inputRef = useRef(null);
  const dragStart = useRef(null);
  const resizeStart = useRef(null);

  const x = (layer.x || 0) * SX;
  const y = (layer.y || 0) * SY;
  const w = (layer.width || 100) * SX;
  const h = (layer.height || 100) * SY;

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleMouseDown = (e) => {
    if (isPlaying) return;
    e.stopPropagation();
    onSelect();
    setDragging(true);
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, origX: layer.x || 0, origY: layer.y || 0 };
  };

  // ── Drag logic ──
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const ds = dragStart.current;
      if (!ds) return;
      const dx = (e.clientX - ds.mouseX) / SX;
      const dy = (e.clientY - ds.mouseY) / SY;
      onUpdate(layer.id, { x: Math.round(ds.origX + dx), y: Math.round(ds.origY + dy) });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, layer.id, onUpdate, SX, SY]);

  // ── Resize logic ──
  const handleResizeStart = (e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(handle);
    resizeStart.current = {
      mouseX: e.clientX, mouseY: e.clientY,
      origX: layer.x || 0, origY: layer.y || 0,
      origW: layer.width || 100, origH: layer.height || 100,
    };
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const rs = resizeStart.current;
      if (!rs) return;
      const dx = (e.clientX - rs.mouseX) / SX;
      const dy = (e.clientY - rs.mouseY) / SY;

      let newX = rs.origX, newY = rs.origY, newW = rs.origW, newH = rs.origH;
      const MIN = 20;

      // Horizontal
      if (resizing.includes('e')) { newW = Math.max(MIN, rs.origW + dx); }
      if (resizing.includes('w')) { newW = Math.max(MIN, rs.origW - dx); newX = rs.origX + rs.origW - newW; }
      // Vertical
      if (resizing.includes('s')) { newH = Math.max(MIN, rs.origH + dy); }
      if (resizing.includes('n')) { newH = Math.max(MIN, rs.origH - dy); newY = rs.origY + rs.origH - newH; }

      onUpdate(layer.id, { x: Math.round(newX), y: Math.round(newY), width: Math.round(newW), height: Math.round(newH) });
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizing, layer.id, onUpdate, SX, SY]);

  const handleDoubleClick = () => {
    if (layer.type === 'text' && !isPlaying) setEditing(true);
  };

  const commitEdit = (value) => {
    onUpdate(layer.id, { content: value });
    setEditing(false);
  };

  // ── Resize handles config ──
  const HANDLES = [
    { id: 'nw', cursor: 'nw-resize', style: { top: -4, left: -4 } },
    { id: 'n',  cursor: 'n-resize',  style: { top: -4, left: '50%', transform: 'translateX(-50%)' } },
    { id: 'ne', cursor: 'ne-resize', style: { top: -4, right: -4 } },
    { id: 'e',  cursor: 'e-resize',  style: { top: '50%', right: -4, transform: 'translateY(-50%)' } },
    { id: 'se', cursor: 'se-resize', style: { bottom: -4, right: -4 } },
    { id: 's',  cursor: 's-resize',  style: { bottom: -4, left: '50%', transform: 'translateX(-50%)' } },
    { id: 'sw', cursor: 'sw-resize', style: { bottom: -4, left: -4 } },
    { id: 'w',  cursor: 'w-resize',  style: { top: '50%', left: -4, transform: 'translateY(-50%)' } },
  ];

  const renderHandles = () => {
    if (!isSelected || isPlaying || editing) return null;
    return HANDLES.map(h => (
      <div
        key={h.id}
        className="absolute w-2.5 h-2.5 bg-white border-2 border-purple-500 rounded-sm z-10"
        style={{ ...h.style, cursor: h.cursor, pointerEvents: 'auto' }}
        onMouseDown={(e) => handleResizeStart(e, h.id)}
        data-testid={`resize-${h.id}-${layer.id}`}
      />
    ));
  };

  const baseStyle = {
    position: 'absolute', left: x, top: y, width: w,
    transform: `rotate(${layer.rotation || 0}deg)`,
    opacity: layer.opacity ?? 1,
    pointerEvents: isPlaying ? 'none' : 'auto',
    userSelect: 'none',
  };

  if (layer.type === 'rect' || layer.type === 'circle') {
    const rectStyle = {
      ...baseStyle, height: h,
      backgroundColor: layer.fill || '#8B5CF6',
      borderRadius: layer.type === 'circle' ? '50%' : `${(layer.borderRadius || 0) * SX}px`,
    };
    if (layer.stroke && layer.strokeWidth) {
      rectStyle.border = `${layer.strokeWidth * SX}px solid ${layer.stroke}`;
    }
    return (
      <div style={rectStyle}
        className={isSelected ? 'ring-1 ring-purple-500 ring-offset-0' : ''}
        onMouseDown={handleMouseDown}
        data-testid={`canvas-layer-${layer.id}`}>
        {renderHandles()}
      </div>
    );
  }

  // Text layer
  const fontSize = Math.max(8, (layer.fontSize || 48) * SX);
  const textAlign = layer.textAlign || 'center';
  const lineHeight = layer.lineHeight || 1.2;
  const fontFamily = layer.fontFamily || 'Montserrat, sans-serif';
  const fontWeight = layer.fontWeight || 'bold';
  const fontStyle = layer.fontStyle || 'normal';
  const textDecoration = layer.textDecoration || 'none';
  const textTransform = layer.textTransform || 'none';
  const shadowObj = layer.shadow || {};
  const textShadow = shadowObj.color
    ? `${shadowObj.offsetX || 2}px ${shadowObj.offsetY || 2}px ${shadowObj.blur || 4}px ${shadowObj.color}`
    : 'none';

  const bgHL = layer.bgHighlight || {};
  const bgStyle = bgHL.enabled ? {
    backgroundColor: `${bgHL.color || '#000'}`,
    opacity: bgHL.opacity ?? 0.6,
    padding: `${(bgHL.padding || 12) * SX}px`,
    borderRadius: `${(bgHL.borderRadius || 0) * SX}px`,
  } : {};

  const displayContent = (() => {
    let c = layer.content || '';
    if (textTransform === 'uppercase') c = c.toUpperCase();
    return c;
  })();

  return (
    <div style={{ ...baseStyle, ...bgStyle, height: 'auto', minHeight: h, textAlign, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      className={`whitespace-pre-wrap ${isSelected ? 'ring-1 ring-purple-500 ring-offset-0' : ''}`}
      onMouseDown={handleMouseDown} onDoubleClick={handleDoubleClick}
      data-testid={`canvas-layer-${layer.id}`}>
      {renderHandles()}
      {editing ? (
        <textarea ref={inputRef} defaultValue={layer.content || ''}
          className="bg-transparent outline-none w-full resize-none"
          style={{ fontSize: `${fontSize}px`, fontFamily, fontWeight, fontStyle, textDecoration, color: layer.fontColor || 'white', textAlign, lineHeight, textShadow }}
          rows={3}
          onBlur={(e) => commitEdit(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
          data-testid="canvas-text-input" />
      ) : (
        <span style={{ fontSize: `${fontSize}px`, fontFamily, fontWeight, fontStyle, textDecoration, textTransform, color: layer.fontColor || 'white', lineHeight, textShadow, display: 'block', textAlign }}>
          {displayContent || 'Text'}
        </span>
      )}
    </div>
  );
};

/* ── Background Image (Konva) ── */
const BackgroundImage = ({ url, canvasW, canvasH, isSelected, onSelect, transform, onTransformUpdate }) => {
  const image = useKonvaImage(url);
  const imageRef = useRef(null);
  const trRef = useRef(null);

  useEffect(() => {
    if (trRef.current && imageRef.current && isSelected) {
      trRef.current.nodes([imageRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  if (!image) return null;

  const userScale = transform?.scale || 1;
  const natW = image.naturalWidth || image.width;
  const natH = image.naturalHeight || image.height;
  const coverScale = Math.max(canvasW / natW, canvasH / natH);
  const s = coverScale * userScale;

  return (
    <>
      <KImage
        ref={imageRef}
        image={image}
        width={natW} height={natH}
        offsetX={natW / 2} offsetY={natH / 2}
        x={(transform?.x || 0) + canvasW / 2}
        y={(transform?.y || 0) + canvasH / 2}
        scaleX={s} scaleY={s}
        rotation={transform?.rotation || 0}
        draggable={isSelected}
        onClick={(e) => { e.cancelBubble = true; onSelect(); }}
        onDragEnd={(e) => {
          onTransformUpdate({
            ...transform,
            x: e.target.x() - canvasW / 2,
            y: e.target.y() - canvasH / 2,
          });
        }}
      />
      <Transformer
        ref={trRef}
        keepRatio centeredScaling rotateEnabled
        borderStroke="#7c3aed" borderStrokeWidth={1.5}
        anchorFill="#7c3aed" anchorStroke="#ffffff" anchorSize={8}
        enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
        visible={isSelected}
      />
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   SlideCanvas — Renders a single carousel slide
   ═══════════════════════════════════════════════════════════ */

export const SlideCanvas = ({
  slide, canvasW = 340, canvasH = 340, designW = DESIGN_W, designH = DESIGN_H,
  selectedLayerId, onSelectLayer, onUpdateLayer,
  onBgSelect, isBgSelected, onBgTransformUpdate,
}) => {
  const SX = canvasW / designW;
  const SY = canvasH / designH;
  const layers = slide?.layers || [];

  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage()) {
      if (onSelectLayer) onSelectLayer(null);
      if (onBgSelect) onBgSelect();
    }
  }, [onSelectLayer, onBgSelect]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (onSelectLayer) onSelectLayer(null);
      if (onBgSelect) onBgSelect();
    }
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden shadow-xl select-none"
      style={{ width: canvasW, height: canvasH }} data-testid="slide-canvas">
      {/* Background: Konva for images, div for solid colors */}
      {slide?.background_image ? (
        <Stage width={canvasW} height={canvasH} onClick={handleStageClick}>
          <Layer>
            <BackgroundImage
              url={slide.background_image}
              canvasW={canvasW} canvasH={canvasH}
              isSelected={isBgSelected}
              onSelect={onBgSelect || (() => {})}
              transform={slide.bg_transform || {}}
              onTransformUpdate={onBgTransformUpdate || (() => {})}
            />
          </Layer>
        </Stage>
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: slide?.background_color || '#1a1a2e' }}
          onClick={handleOverlayClick} />
      )}

      {/* Text/Shape layers overlay */}
      <div className="absolute inset-0" onClick={handleOverlayClick}>
        {layers.map(layer => (
          <InteractiveLayer
            key={layer.id}
            layer={layer}
            SX={SX} SY={SY}
            isSelected={selectedLayerId === layer.id}
            onSelect={() => onSelectLayer && onSelectLayer(layer.id)}
            onUpdate={onUpdateLayer || (() => {})}
            isPlaying={false}
          />
        ))}
      </div>
    </div>
  );
};
